import React from "react";

/**
 * Compact SVG sparkline. Renders a smooth mini line + area fill. Zero deps
 * (no recharts) to keep it lightweight so multiple KPI cards can each show
 * one without cost.
 *
 * `points`: array of numbers (any range — auto-normalised)
 * `tone`:   info | success | warning | danger | brand
 */
const TONE_STROKE = {
  brand:   "#7C6BFF",
  info:    "#4CB4FF",
  success: "#34D399",
  warning: "#F5B547",
  danger:  "#F26E7E",
};

export default function Sparkline({
  points = [], width = 100, height = 28, tone = "brand", showArea = true,
}) {
  if (!points || points.length < 2) return null;
  const min = Math.min(...points);
  const max = Math.max(...points);
  const range = max - min || 1;
  const stepX = width / (points.length - 1);

  const coords = points.map((p, i) => {
    const x = i * stepX;
    const y = height - ((p - min) / range) * (height - 4) - 2;
    return [x, y];
  });

  const pathD = coords
    .map((c, i) => (i === 0 ? `M ${c[0]},${c[1]}` : `L ${c[0]},${c[1]}`))
    .join(" ");

  const areaD = `${pathD} L ${width},${height} L 0,${height} Z`;

  const color = TONE_STROKE[tone] || TONE_STROKE.brand;
  const gradId = `hbf-spark-${tone}-${Math.random().toString(36).slice(2, 8)}`;

  return (
    <svg
      className="hbf-sparkline"
      width={width}
      height={height}
      viewBox={`0 0 ${width} ${height}`}
      aria-hidden
    >
      <defs>
        <linearGradient id={gradId} x1="0" x2="0" y1="0" y2="1">
          <stop offset="0%"   stopColor={color} stopOpacity="0.36" />
          <stop offset="100%" stopColor={color} stopOpacity="0" />
        </linearGradient>
      </defs>
      {showArea && <path d={areaD} fill={`url(#${gradId})`} />}
      <path d={pathD} fill="none" stroke={color} strokeWidth="1.6" />
    </svg>
  );
}
